import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AxiosInstance, AxiosStatic } from 'axios';
import { AppDispatch, RootState } from '../store'; // Adjust the path as necessary
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from './notificationSlice';
import { NotificationType } from '../../components/notifications/notification-component';
import { clear } from 'console';

export interface MediaState {
  isProcessing: boolean;
  queue: {
    collisionId: string;
    vehicleId: string;
    fileId: string;
  }[];
  images: string[];
  currentMediaName: string;
  currentImageProgress: number;
  currentImageSize: number;
  isUploading: boolean;
  isLoading: boolean;
  error: string | null;
}

export const initialState: MediaState = {
  isProcessing: false,
  queue: [],
  images: [],
  currentMediaName: '',
  currentImageProgress: 0,
  currentImageSize: 0,
  isUploading: false,
  isLoading: false,
  error: null,
};

export const fetchMediaZipByCollisionId = createAsyncThunk(
  'collision/fetchMediaZipByCollisionId',
  async (
    { id, csrService }: { id: number; csrService: AxiosInstance },
    { rejectWithValue },
  ) => {
    try {
      const response = await csrService.get(
        `/api/v1/media/images/collisions/${id}/zip`,
        {
          timeout: 100 * 1000,
          responseType: 'blob',
        },
      );

      const blob = response.data;
      const fileURL = window.URL.createObjectURL(blob);

      const link = document.createElement('a');

      link.href = fileURL;
      link.setAttribute('download', `${id}_images.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return 'Download complete';
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchMediaListByCollisionId = createAsyncThunk(
  'collision/fetchMediaByCollisionId',
  async (
    { id, csrService }: { id: number; csrService: AxiosInstance },
    { rejectWithValue },
  ) => {
    try {
      const response = await csrService.get(
        `/api/v1/media/images/collisions/${id}`,
        {
          timeout: 100 * 1000,
        },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const uploadMedia = createAsyncThunk(
  'media/deleteMedia',
  async (
    {
      collisionId,
      vehicleId,
      file,
      csrService,
      dispatch,
    }: {
      collisionId: string;
      vehicleId: string;
      file: File;
      csrService: AxiosInstance;
      dispatch: AppDispatch;
    },
    { rejectWithValue },
  ) => {
    try {
      dispatch(updateCurrentMediaName(file.name));
      dispatch(updateImageSize(file.size));

      await csrService.post(
        `/api/v1/media/images/collisions/${collisionId}/vehicle/${vehicleId}/${file.name}`,
        file,
        {
          timeout: 100 * 1000,
          headers: {
            'Content-Type': `${file.type}`,
          },
        },
      );
    } catch (error: any) {
      console.error('Error uploading media: ', error);
      if (error.response.data.error == 'Maximum number of images exceeded') {
        dispatch(removeAllValidationErrorNotifications());
        dispatch(
          pushNotification({
            body: 'Max number of images exceeded',
            notificationType: NotificationType.importantValidationError,
          }),
        );
        return rejectWithValue('Max number of images exceeded');
      }

      if (error.response.data.error == 'The file is already uploaded.') {
        dispatch(removeAllValidationErrorNotifications());
        dispatch(
          pushNotification({
            body: 'The file is already uploaded. Please choose a different file',
            notificationType: NotificationType.importantValidationError,
          }),
        );
        return rejectWithValue(
          'The file is already uploaded. Please choose a different file',
        );
      }

      dispatch(
        pushNotification({
          body: 'Error uploading media',
          notificationType: NotificationType.importantValidationError,
        }),
      );
      return rejectWithValue('Error uploading media');
    }
  },
);

export const deleteMedia = createAsyncThunk(
  'media/deleteMedia',
  async (
    {
      imageName,
      csrService,
    }: {
      imageName: string;
      csrService: AxiosInstance;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await csrService.delete(
        `/api/v1/media/images/collisions/${imageName}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    addToQueue(state, action) {
      state.queue.push(action.payload);
    },
    getMediaList(state, action: PayloadAction<string[]>) {
      state.images = [...state.images, ...action.payload];
      return state;
    },
    updateCurrentMediaName(state, action: PayloadAction<string>) {
      state.currentMediaName = action.payload;
      return state;
    },
    updateProgress(state, action: PayloadAction<number>) {
      state.currentImageProgress = action.payload;
      return state;
    },
    updateImageSize(state, action: PayloadAction<number>) {
      state.currentImageSize = action.payload;
      return state;
    },
    dismissError(state) {
      state.error = null;
      return state;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMediaZipByCollisionId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchMediaZipByCollisionId.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
        },
      )
      .addCase(
        fetchMediaZipByCollisionId.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload;
        },
      )
      .addCase(uploadMedia.pending, (state) => {
        state.isUploading = true;
        state.error = null;
      })
      .addCase(uploadMedia.fulfilled, (state, aciton) => {
        state.isUploading = false;
        state.queue.shift();
      })
      .addCase(uploadMedia.rejected, (state, action: PayloadAction<any>) => {
        state.isUploading = false;
        state.queue = [];
        state.error = action.payload;
      })
      .addCase(fetchMediaListByCollisionId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchMediaListByCollisionId.fulfilled,
        (state, action: PayloadAction<{ images: string[] }>) => {
          state.images = [...action.payload.images];
          state.isLoading = false;
        },
      )
      .addCase(
        fetchMediaListByCollisionId.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload;
        },
      );
  },
});

export const {
  addToQueue,
  getMediaList,
  updateCurrentMediaName,
  updateProgress,
  updateImageSize,
  dismissError,
} = mediaSlice.actions;

export default mediaSlice.reducer;
